import { useState } from "react";
import {
  createAdsPostTagDef,
  deleteAdsPostTagDef,
  updateAdsPostTagDef,
  type AdsAssistantPostTagDefRow,
} from "@/lib/adsAssistantClient";
import { AdsTagKindChip, AdsTagKindRadio, type TagKind } from "./AdsTagKindBadges";

export function AdsLibraryPostTagsPanel(props: {
  canEdit: boolean;
  postTagDefs: AdsAssistantPostTagDefRow[];
  postTagSlug: string;
  setPostTagSlug: (v: string) => void;
  postTagLabel: string;
  setPostTagLabel: (v: string) => void;
  postTagSortDraft: string;
  setPostTagSortDraft: (v: string) => void;
  postTagKind: TagKind;
  setPostTagKind: (v: TagKind) => void;
  setError: (e: string | null) => void;
  setBanner: (b: string | null) => void;
  load: () => Promise<void>;
}) {
  const {
    canEdit,
    postTagDefs,
    postTagSlug,
    setPostTagSlug,
    postTagLabel,
    setPostTagLabel,
    postTagSortDraft,
    setPostTagSortDraft,
    postTagKind,
    setPostTagKind,
    setError,
    setBanner,
    load,
  } = props;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSlug, setEditSlug] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [editSort, setEditSort] = useState("0");
  const [editKind, setEditKind] = useState<TagKind>("topic");

  const parseSort = (raw: string): number => {
    const n = parseInt(raw.trim(), 10);
    return Number.isFinite(n) ? n : 0;
  };

  const sortedDefs = [...postTagDefs].sort((a, b) => {
    const ka = a.kind === "archetype" ? 0 : 1;
    const kb = b.kind === "archetype" ? 0 : 1;
    if (ka !== kb) return ka - kb;
    const sa = a.sort_order ?? 0;
    const sb = b.sort_order ?? 0;
    if (sa !== sb) return sa - sb;
    return a.label.localeCompare(b.label);
  });

  const beginEdit = (row: AdsAssistantPostTagDefRow) => {
    setEditingId(row.id);
    setEditSlug(row.slug);
    setEditLabel(row.label);
    setEditSort(String(row.sort_order ?? 0));
    setEditKind(row.kind === "archetype" ? "archetype" : "topic");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditSlug("");
    setEditLabel("");
    setEditSort("0");
    setEditKind("topic");
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-600 dark:bg-zinc-900">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Post tags</h4>
      <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">
        Vocabulary for ingest chips and index filters. Deleting a tag removes it from every post (links use ON DELETE CASCADE).
      </p>

      {editingId ? (
        <div className="mt-3 space-y-2 rounded-lg border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-900/50 dark:bg-blue-950/20">
          <p className="text-xs font-medium text-gray-900 dark:text-gray-100">Edit tag</p>
          <input
            className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-800"
            placeholder="slug"
            value={editSlug}
            onChange={(e) => setEditSlug(e.target.value)}
          />
          <input
            className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-800"
            placeholder="Label"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
          />
          <input
            className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-800"
            placeholder="Sort order"
            value={editSort}
            onChange={(e) => setEditSort(e.target.value)}
          />
          <AdsTagKindRadio value={editKind} onChange={setEditKind} idPrefix="ads-post-tag-edit" />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-lg bg-blue-700 px-2 py-1.5 text-xs font-medium text-white hover:bg-blue-800"
              onClick={async () => {
                const r = await updateAdsPostTagDef(editingId, {
                  slug: editSlug.trim().toLowerCase(),
                  label: editLabel.trim(),
                  sort_order: parseSort(editSort),
                  kind: editKind,
                });
                if (!r.ok) {
                  setError(r.error);
                  return;
                }
                setBanner("Post tag updated.");
                cancelEdit();
                void load();
              }}
            >
              Save
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-zinc-800"
              onClick={cancelEdit}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <ul className="mt-3 max-h-52 space-y-1 overflow-y-auto text-sm">
        {sortedDefs.map((row) => (
          <li
            key={row.id}
            className="flex flex-wrap items-center justify-between gap-1 rounded border border-white bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
          >
            <span className="min-w-0">
              <span className="font-medium text-gray-900 dark:text-gray-100">{row.label}</span>{" "}
              <code className="text-[10px] text-gray-500 dark:text-gray-400">{row.slug}</code>
              <AdsTagKindChip kind={row.kind} />
              <span className="ml-1 text-[10px] text-gray-400 tabular-nums">sort {row.sort_order ?? 0}</span>
            </span>
            {canEdit ? (
              <span className="flex shrink-0 gap-2">
                <button
                  type="button"
                  className="text-[10px] text-blue-700 hover:underline dark:text-blue-400"
                  disabled={!!editingId}
                  onClick={() => beginEdit(row)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="text-[10px] text-red-700 hover:underline dark:text-red-400"
                  disabled={!!editingId}
                  onClick={() => {
                    const ok = window.confirm(
                      "Delete this tag? It will be removed from every indexed post that uses it (links are cleared).",
                    );
                    if (!ok) return;
                    void (async () => {
                      const r = await deleteAdsPostTagDef(row.id);
                      if (!r.ok) {
                        setError(r.error);
                        return;
                      }
                      setBanner("Post tag removed.");
                      void load();
                    })();
                  }}
                >
                  Remove
                </button>
              </span>
            ) : null}
          </li>
        ))}
        {!postTagDefs.length ? <li className="text-xs text-gray-500 dark:text-gray-400">No post tags yet.</li> : null}
      </ul>

      {canEdit && !editingId ? (
        <div className="mt-3 space-y-2 border-t border-gray-200 pt-3 dark:border-gray-600">
          <input
            className="w-full rounded border border-gray-200 px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-800"
            placeholder="slug (e.g. seasonal_sale)"
            value={postTagSlug}
            onChange={(e) => setPostTagSlug(e.target.value)}
          />
          <input
            className="w-full rounded border border-gray-200 px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-800"
            placeholder="Label"
            value={postTagLabel}
            onChange={(e) => setPostTagLabel(e.target.value)}
          />
          <input
            className="w-full rounded border border-gray-200 px-2 py-1 text-xs dark:border-gray-600 dark:bg-zinc-800"
            placeholder="Sort order (default 0)"
            value={postTagSortDraft}
            onChange={(e) => setPostTagSortDraft(e.target.value)}
          />
          <AdsTagKindRadio value={postTagKind} onChange={setPostTagKind} idPrefix="ads-post-tag-add" />
          <button
            type="button"
            className="w-full rounded-lg bg-blue-700 px-2 py-1.5 text-xs font-medium text-white hover:bg-blue-800"
            onClick={async () => {
              const r = await createAdsPostTagDef({
                slug: postTagSlug.trim().toLowerCase(),
                label: postTagLabel.trim(),
                sort_order: parseSort(postTagSortDraft),
                kind: postTagKind,
              });
              if (!r.ok) {
                setError(r.error);
                return;
              }
              setPostTagSlug("");
              setPostTagLabel("");
              setPostTagSortDraft("0");
              setPostTagKind("topic");
              setBanner("Post tag added.");
              void load();
            }}
          >
            Add post tag
          </button>
        </div>
      ) : null}
    </div>
  );
}
